import get from 'lodash.get';
import React, { useContext, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { IoIosCheckmarkCircle } from 'react-icons/io';
import { toast } from 'react-toastify';

import apiUtil from 'src/api/activation';
import CustomToast from 'src/components/CustomToast';
import { AppContext } from 'src/containers/Core/AppContext';
import { AppState } from 'src/containers/Core/AppReducer';
import { SUPPORT_ERROR, SUPPORT_SUCCESS } from 'src/containers/Core/Messages';
import { BUSINESS_ONBOARDING_STEP } from '..';

import './style.scss';

type SuccessModalProps = {
  onHide: VoidFunction;
  showSuccessModal: PosIndentifierResponse;
  setOnboardingStep: (step: BUSINESS_ONBOARDING_STEP) => void;
  onConflict: (conflict: ConflictIdentifierResponse) => void;
};

const SuccessModal: React.FC<SuccessModalProps> = ({ showSuccessModal, onHide, setOnboardingStep, onConflict }) => {
  if (!showSuccessModal) {
    return null;
  }

  const [loading, setLoading] = useState(false);
  const { store } = useContext(AppContext);
  const { businessOnboardingDetails }: AppState = store;

  const businessUuid = get(businessOnboardingDetails, 'business.uuid');

  const onNext = async () => {
    try {
      setLoading(true);
      await apiUtil.postConfirmMapping(businessUuid);
      onHide();
      setOnboardingStep(BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_COMMUNITY);
    } catch (error) {
      if (error.response.status === 409) {
        onConflict(error.response.data);
        onHide();
      } else {
        toast(<CustomToast description={get(error, 'response.data.error')} />);
      }
    } finally {
      setLoading(false);
    }
  };

  const contactSupportClick = async () => {
    try {
      await apiUtil.postContactSupport(businessUuid, get(showSuccessModal, 'posIdentifiers[0]'));
      toast(<CustomToast type="success" title="Request to Contact Support" description={SUPPORT_SUCCESS} />, {
        className: 'success',
      });
    } catch {
      toast(<CustomToast title="Contact Support" description={SUPPORT_ERROR} />);
    }
  };

  return (
    <Modal show={true} onHide={onHide} backdrop="static">
      <Modal.Body>
        <div className="d-flex flex-column align-items-center justify-content-center p-3">
          <IoIosCheckmarkCircle size={50} className="purple-color" />
          <span className="text-muted w-75 my-3 text-center">
            Thank you! We have linked the following POS information to your business
          </span>
          <strong className="font-size-16">{get(showSuccessModal, 'posIdentifiers[0]')}</strong>
          <small className="text-muted w-75 my-3 text-center">
            If this is not the correct statement name, please
            <span className="royal-blue-color c-pointer ml-1" onClick={contactSupportClick}>
              Contact Support
            </span>
          </small>
          <Button className="mt-3" onClick={onNext}>
            {loading ? <Spinner animation="border" size="sm" className="mx-3" /> : 'Next'}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SuccessModal;
